import React, { memo } from 'react';
import { EdgeText, Position } from 'reactflow';
import Style from './CustomEdges.module.css';
import { EDGE_COLOR, UNSAVED_EDGE_COLOR } from '../edgeColors';

// Get bezier path for the edge curve
const getBezierEdgePath = ({ 
  sourceX, 
  sourceY, 
  sourcePosition, 
  targetX, 
  targetY, 
  targetPosition 
}) => {
  // Calculate control points for the bezier curve
  const deltaX = Math.abs(targetX - sourceX) * 0.5;
  const sourceControlX = sourcePosition === Position.Left ? sourceX - deltaX : sourceX + deltaX;
  const targetControlX = targetPosition === Position.Right ? targetX + deltaX : targetX - deltaX;
  
  // Calculate the center point for label positioning
  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;
  
  // Create the path with cubic bezier curve
  const path = `M ${sourceX},${sourceY} C ${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`;
  
  return [path, centerX, centerY];
};

// Custom edge with transform icon
export const CustomEdge = memo(({ 
  id, 
  source, 
  target, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  sourcePosition, 
  targetPosition, 
  data, 
  style, 
  markerEnd,
  animated
}) => {
  const [edgePath, labelX, labelY] = getBezierEdgePath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Default styles if not provided
  const edgeStyle = {
    stroke: EDGE_COLOR,
    strokeWidth: 2,
    ...style,
    // Drawn but not saved yet
    ...(data?.unsaved ? { stroke: UNSAVED_EDGE_COLOR } : {})
  };

  // Determine if the edge has a transformation
  const hasTransformation = data?.hasTransformation || data?.transformationType !== 'none';

  // Skip labels for tree structure edges (those without data)
  const isTreeEdge = !data || Object.keys(data || {}).length === 0 || id.startsWith('tree-edge-');

  return (
    <>
      <path
        id={id}
        style={edgeStyle}
        className={`react-flow__edge-path ${animated ? 'react-flow__edge-path-animated' : ''}`}
        d={edgePath}
        markerEnd={markerEnd}
      />
      {!isTreeEdge && data?.label && (
        <EdgeText
          x={labelX}
          y={labelY}
          label={data.label}
          labelStyle={data.labelStyle || { fill: '#1e54b8', fontWeight: 600, fontSize: 12 }}
          labelBgStyle={data.labelBgStyle || { fill: '#ffffff', fillOpacity: 0.8, rx: 4, ry: 4 }}
          labelBgPadding={data.labelBgPadding || [4, 2]}
        />
      )}
      {!isTreeEdge && hasTransformation && (
        <foreignObject
          width={24}
          height={24}
          x={labelX - 12}
          y={labelY - 30}
          className="edgebutton-foreignobject"
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <div className={Style.transformBadge}>
            <i className="uil-setting" aria-hidden="true" />
          </div>
        </foreignObject>
      )}
    </>
  );
});

export default CustomEdge;